/* =====================================================================
 * GuideToc — 사용자 가이드 왼쪽 목차 패널.
 *
 * - 섹션/항목은 메인 사이드바를 그대로 비춘다(`UserGuidePage` 가 조립).
 * - 검색: 항목 이름, 각 문서의 절 제목과 절 본문을 함께 찾는다. "시스템 프롬프트"
 *   를 치면 그 설명이 들어 있는 "통합 에이전트 › 자주 쓰는 지시 저장하기" 가 나온다.
 * - 절이 있는 문서는 오른쪽 화살표로 절 목록을 열고 닫는다. 문서를 고르면 자동으로
 *   열리고, 읽고 있는 절을 가지선 위에 표시한다.
 * - `comingSoonSectionKeys` 로 지정한 섹션에서만, 문서가 없는 항목에 "준비 중" 을 붙인다.
 *   그 밖의 섹션은 문서를 순서대로 채워 넣는 중이라 표시하지 않는다.
 * ===================================================================== */

import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, ChevronsDownUp, ChevronsUpDown, Menu, PanelLeft, Search, X } from 'lucide-react';

import type { GuideHeading } from './guideOutline';
import type { GuideEntry, GuideSection } from './guideModel';
import * as S from './GuideToc.styles';

export interface GuideTocProps {
  readonly sections: readonly GuideSection[];
  readonly activeKey: string | null;
  readonly onSelect: (key: string, headingId?: string) => void;
  /** 항목 키 → 그 문서의 절 목록. 문서가 없는 항목은 키가 없다. */
  readonly headingsByKey: Readonly<Record<string, readonly GuideHeading[]>>;
  readonly activeHeadingId: string | null;
  readonly open: boolean;
  readonly onToggle: () => void;
  readonly mainSidebarOpen: boolean;
  readonly onOpenMainSidebar: () => void;
  /** 문서가 없을 때 "준비 중" 을 보여줄 섹션 키. */
  readonly comingSoonSectionKeys?: ReadonlySet<string>;
}

const normalize = (value: string) => value.toLocaleLowerCase('ko').replace(/\s+/g, '');

const headingMatches = (heading: GuideHeading, q: string) =>
  normalize(heading.text).includes(q) || normalize(heading.searchText).includes(q);

/** 검색어가 들어 있는 부분만 강조해서 돌려준다. 공백은 무시하고 맞춘다. */
function highlight(text: string, query: string): React.ReactNode {
  if (!query) return text;
  const compact = normalize(query);
  /* 원문 글자 위치 ↔ 공백 뺀 위치 대응표를 만들어 강조 범위를 되돌린다. */
  const map: number[] = [];
  let flat = '';
  for (let i = 0; i < text.length; i += 1) {
    if (/\s/.test(text[i])) continue;
    map.push(i);
    flat += text[i].toLocaleLowerCase('ko');
  }
  const at = flat.indexOf(compact);
  if (at < 0) return text;
  const start = map[at];
  const end = map[at + compact.length - 1] + 1;
  return (
    <>
      {text.slice(0, start)}
      <S.HeadingMatch>{text.slice(start, end)}</S.HeadingMatch>
      {text.slice(end)}
    </>
  );
}

export const GuideToc: React.FC<GuideTocProps> = ({
  sections,
  activeKey,
  onSelect,
  headingsByKey,
  activeHeadingId,
  open,
  onToggle,
  mainSidebarOpen,
  onOpenMainSidebar,
  comingSoonSectionKeys,
}) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const searching = query.trim().length > 0;

  /* 섹션 접힘 — 섹션 목록이 테넌트 설정에 따라 달라지므로 "닫힌 것만" 기록한다.
   * 기록에 없는 섹션은 펼침이라 나중에 섹션이 추가돼도 초기화가 필요 없다. */
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  /* 문서별 절 목록 펼침. 고른 문서는 자동으로 펼치고, 화살표로 언제든 접는다.
   * 다른 문서를 골라도 앞서 펼친 문서를 억지로 접지 않는다. */
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  useEffect(() => {
    if (activeKey) setExpanded((prev) => (prev[activeKey] ? prev : { ...prev, [activeKey]: true }));
  }, [activeKey]);
  const toggleExpanded = (key: string) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  const toggleSection = (key: string) => setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));

  /* 목차 머리의 "모두 펼치기 / 모두 접기". 문서마다 절 목록을 열다 보면 목차가 길어져서
   * 한 번에 정리할 수단을 둔다. 하나라도 열려 있으면 접기, 모두 닫혀 있으면 펼치기다.
   * 펼칠 때는 접어 둔 섹션도 함께 연다 — 섹션이 닫혀 있으면 펼친 절이 보이지 않는다. */
  const entriesWithSections = sections.flatMap((section) =>
    section.entries.filter((entry) => headingsByKey[entry.key]?.length),
  );
  const anyExpanded = entriesWithSections.some((entry) => expanded[entry.key]);
  const toggleAll = () => {
    if (anyExpanded) {
      setExpanded({});
      return;
    }
    setCollapsed({});
    setExpanded(Object.fromEntries(entriesWithSections.map((entry) => [entry.key, true])));
  };
  const toggleAllLabel = t(anyExpanded ? 'userGuide.collapseAll' : 'userGuide.expandAll');

  const visibleSections = useMemo(() => {
    if (!searching) return sections;
    const q = normalize(query);
    return sections
      .map((section) => ({
        ...section,
        entries: section.entries.filter(
          (entry) =>
            normalize(entry.label).includes(q) ||
            (headingsByKey[entry.key] ?? []).some((heading) => headingMatches(heading, q)),
        ),
      }))
      .filter((section) => section.entries.length > 0);
  }, [sections, searching, query, headingsByKey]);

  const renderHeadings = (entry: GuideEntry) => {
    const headings = headingsByKey[entry.key] ?? [];
    const isActive = entry.key === activeKey;
    const shown = searching
      ? headings.filter((heading) => headingMatches(heading, normalize(query)))
      : expanded[entry.key]
        ? headings
        : [];
    if (shown.length === 0) return null;
    return (
      <S.HeadingList>
        {shown.map((heading) => {
          const current = isActive && heading.id === activeHeadingId;
          return (
            <li key={heading.id}>
              <S.HeadingLink
                type="button"
                $active={current}
                aria-current={current ? 'location' : undefined}
                onClick={() => onSelect(entry.key, heading.id)}
              >
                {searching ? highlight(heading.text, query) : heading.text}
              </S.HeadingLink>
            </li>
          );
        })}
      </S.HeadingList>
    );
  };

  return (
    <>
      <S.MobileOverlay $open={open} onClick={onToggle} />
      <S.TocPane $open={open}>
        <S.TocPaneInner $open={open}>
          <S.Toc aria-label={t('userGuide.contents')}>
            <S.TocHead>
              <S.TocHeadLeft>
                {/* 메인 사이드바를 접은 뒤 다시 펼치는 유일한 버튼. 이 페이지는
                  * frameless(공용 TopBar 없음)라 여기서 직접 그린다. */}
                {!mainSidebarOpen && (
                  <S.TocIconButton
                    type="button"
                    onClick={onOpenMainSidebar}
                    title={t('workspace.expandMainPanel')}
                    aria-label={t('workspace.expandMainPanel')}
                  >
                    <PanelLeft size={16} />
                  </S.TocIconButton>
                )}
                <S.TocTitle>{t('userGuide.contents')}</S.TocTitle>
              </S.TocHeadLeft>
              <S.TocHeadRight>
                {entriesWithSections.length > 0 && (
                  <S.TocIconButton
                    type="button"
                    onClick={toggleAll}
                    disabled={searching}
                    title={toggleAllLabel}
                    aria-label={toggleAllLabel}
                  >
                    {anyExpanded ? <ChevronsDownUp size={16} /> : <ChevronsUpDown size={16} />}
                  </S.TocIconButton>
                )}
                <S.TocToggleBtn
                  type="button"
                  onClick={onToggle}
                  title={t('workspace.collapsePageSidebar')}
                  aria-label={t('workspace.collapsePageSidebar')}
                >
                  <Menu size={16} />
                </S.TocToggleBtn>
              </S.TocHeadRight>
            </S.TocHead>

            <S.SearchBox>
              <Search size={14} aria-hidden />
              <input
                type="search"
                value={query}
                placeholder={t('userGuide.search')}
                aria-label={t('userGuide.search')}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Escape') setQuery('');
                }}
              />
              {searching && (
                <S.SearchClear type="button" aria-label={t('userGuide.clearSearch')} onClick={() => setQuery('')}>
                  <X size={12} />
                </S.SearchClear>
              )}
            </S.SearchBox>

            <S.TocScroll>
              {visibleSections.length === 0 && <S.EmptyResult>{t('userGuide.noResults')}</S.EmptyResult>}
              {visibleSections.map((section) => {
                /* 제목 없는 섹션(관리자 콘솔 단독 항목)은 접을 손잡이가 없으니 항상
                 * 펼쳐 둔다. 검색 중에는 결과를 가리지 않도록 모두 펼친다. */
                const collapsible = !!section.title && !searching;
                const sectionOpen = !collapsible || !collapsed[section.key];
                return (
                  <React.Fragment key={section.key}>
                    {section.title && (
                      <S.TocSectionHeader
                        role="button"
                        tabIndex={collapsible ? 0 : -1}
                        aria-expanded={sectionOpen}
                        onClick={() => collapsible && toggleSection(section.key)}
                        onKeyDown={(e) => {
                          if (!collapsible || e.target !== e.currentTarget) return;
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggleSection(section.key);
                          }
                        }}
                      >
                        <span>{section.title}</span>
                        {collapsible && (
                          <S.TocSectionChevron>
                            {sectionOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                          </S.TocSectionChevron>
                        )}
                      </S.TocSectionHeader>
                    )}
                    {/* 제목 있는 섹션의 항목은 안쪽으로 들이고 왼쪽 가이드선으로 묶어,
                      * "워크스페이스 안의 통합 에이전트" 처럼 소속이 보이게 한다. */}
                    {sectionOpen && (
                      <S.TocGroup $nested={Boolean(section.title)}>
                      {section.entries.map((entry) => {
                          const isActive = entry.key === activeKey;
                          const hasSections = Boolean(headingsByKey[entry.key]?.length);
                          const isOpen = searching || Boolean(expanded[entry.key]);
                          const comingSoon =
                            !headingsByKey[entry.key] && Boolean(comingSoonSectionKeys?.has(section.key));
                          return (
                            <React.Fragment key={entry.key}>
                              <S.TocRow>
                                <S.TocItem
                                  type="button"
                                  $active={isActive}
                                  $withToggle={hasSections}
                                  aria-current={isActive ? 'page' : undefined}
                                  onClick={() => {
                                    /* 문서 이름은 늘 그 문서로 간다 — 보고 있는 문서면 맨 위로.
                                     * 예전에는 보고 있는 문서를 다시 누르면 절 목록만 접혀서, 아래를
                                     * 읽다가 눌렀을 때 "이동이 안 된다" 로 보였다. 접기는 화살표로 한다. */
                                    if (hasSections) setExpanded((prev) => ({ ...prev, [entry.key]: true }));
                                    onSelect(entry.key);
                                  }}
                                >
                                  <S.TocItemLabel>
                                    {searching ? highlight(entry.label, query) : entry.label}
                                  </S.TocItemLabel>
                                  {comingSoon && <S.TocItemBadge>{t('userGuide.comingSoon')}</S.TocItemBadge>}
                                </S.TocItem>
                                {/* 화살표는 항목 카드 안 오른쪽 끝에 겹쳐 둔다 — 선택된 항목의 흰 카드가
                                  * 화살표까지 감싸 한 줄로 읽힌다. */}
                                {hasSections && (
                                  <S.TocExpandButton
                                    type="button"
                                    $open={isOpen}
                                    disabled={searching}
                                    aria-expanded={isOpen}
                                    aria-label={`${entry.label} ${isOpen ? '접기' : '펼치기'}`}
                                    onClick={() => toggleExpanded(entry.key)}
                                  >
                                    <ChevronRight size={14} />
                                  </S.TocExpandButton>
                                )}
                              </S.TocRow>
                              {renderHeadings(entry)}
                            </React.Fragment>
                          );
                        })}
                      </S.TocGroup>
                    )}
                  </React.Fragment>
                );
              })}
            </S.TocScroll>
          </S.Toc>
        </S.TocPaneInner>
      </S.TocPane>
    </>
  );
};
